import { useEffect, useState } from "react";
import { Link, useLocation, useParams } from "react-router";
import { useAtomValue } from "jotai";
import {
  Telescope,
  Radar,
  MonitorPlay,
  Sparkles,
  NotebookPen,
  FolderGit2,
  AlertTriangle,
  UsersRound,
  Users,
  Boxes,
  History,
  Code2,
  SlidersHorizontal,
  Gem,
  Settings,
  Eye,
  Shield,
  Hash,
  Globe,
  KeyRound,
  Bot,
  ChevronRight,
  type LucideIcon,
} from "lucide-react";
import { authAtom } from "../stores/auth";
import { selectedAccountAtom } from "../stores/galaxy";
import { localeAtom } from "../stores/locale";
import { workerAtom } from "../stores/worker";
import { useIsSharedGuest } from "../lib/hooks/useIsSharedGuest";
import { useT, interp, type MessageKey } from "../lib/i18n";
import { snapshotApi, orgApi, repoApi } from "../api/client";
import { AccountMenu } from "./AccountMenu";
import GalaxySwitcher from "./GalaxySwitcher";
import TeamScopeSwitcher from "./TeamScopeSwitcher";
import { AceLogoMark } from "./AceLogoMark";

// Paths are relative to /:account/
// Icons are lucide components rather than unicode glyphs so every row
// shares one optical width (the old \u25C9\u25C8\u25D6\u25B7 glyphs rendered at
// inconsistent widths and broke vertical alignment). The choices keep
// the telescope/cosmology vocabulary: Observatory\u2192Telescope,
// Constellation\u2192Sparkles (star map), Career\u2192Orbit.
// `enabled: false` renders the row as a disabled "準備中 / Soon" affordance
// instead of a working link (build-time feature flags — Coming Soon in prod).
const navKeys: { path: string; key: MessageKey; icon: LucideIcon; enabled?: boolean }[] = [
  { path: "", key: "nav.observatory", icon: Telescope },
  // Daily Observation is the morning-scan surface — Radar evokes scanning for
  // structural events rather than watching a number tick up.
  { path: "daily", key: "nav.daily", icon: Radar },
  // Ambient is the always-on / standup READ surface — MonitorPlay evokes the
  // wall/TV display that hangs unattended and auto-refreshes. Sibling of Daily,
  // but it answers "what is TRUE right now" (structural facts) rather than "what
  // do I need to fix" (the daily intervention feed).
  { path: "ambient", key: "nav.ambient", icon: MonitorPlay },
  { path: "constellation", key: "nav.constellation", icon: Sparkles },
  { path: "logbook", key: "nav.logbook", icon: NotebookPen },
  // Modules sits directly under the Logbook (sibling observation outputs).
  { path: "modules", key: "nav.modules", icon: Boxes },
  // Teams; Members (org-admin only) is spliced in right below it at render.
  { path: "teams", key: "nav.teams", icon: UsersRound },
  // Career is NOT a galaxy nav row: it's a USER concept (a person's trajectory
  // across the galaxies they've left), not a galaxy attribute. Like Control Room
  // and Feedback it renders inside the galaxy chrome (so the current galaxy keeps
  // its normal sidebar) but its entry point is the account menu below, never this
  // galaxy nav \u2014 and its data is user-scoped (/me/* APIs, JWT-keyed), so it
  // can't leak a person's other-galaxy affiliations into an org's context.
  // Observation Runs sits just above the Control Room \u2014 it's the
  // provenance log you reach for less often than the daily surfaces.
  { path: "runs", key: "nav.runs", icon: History },
  // Badges & Graphs — copy-paste README embeds (badge + profile/team cards).
  { path: "embeds", key: "nav.embeds", icon: Code2 },
  // Repos, Plan and the three settings destinations live inside the 設定
  // accordion (settingsLinks) — configuration surfaces kept below the daily
  // observation work. Control Room / Feedback are account-level and live in
  // the account menu (the identity row at the sidebar bottom).
];

// The 設定 group — an inline accordion that expands downward so the per-galaxy
// nav stays short. It collects the configuration surfaces: which repos are
// observed, the EIS analysis config, the member's OWN Signal Disclosure (D-05
// self opt-in), the galaxy-wide Visibility Mode (admin), and the plan/billing.
// Nav rows a Shared-Galaxies GUEST may reach — the pure observation READS the
// backend allowlist serves (galaxy view/timeline/accumulated, modules, teams,
// public embeds). Everything else (observe triggers, logbook, runs, the whole
// settings group) is owner/write and would only 403, so it's hidden for a guest.
const GUEST_NAV_PATHS = new Set(["", "constellation", "modules", "teams", "embeds"]);

const settingsLinks: { path: string; key: MessageKey; icon: LucideIcon }[] = [
  { path: "repos", key: "nav.repos", icon: FolderGit2 },
  { path: "settings", key: "nav.eis-config", icon: SlidersHorizontal },
  { path: "connectors/slack", key: "nav.slack-connector", icon: Hash },
  { path: "api-tokens", key: "nav.api-tokens", icon: KeyRound },
  // AI Integration — mcp_ token management for the OrbitLens MCP context
  // server (an AI coding agent's read-only connection). Sibling of API Tokens,
  // its own settings destination since it's a different token family/surface.
  // Unlike api-tokens, every mcp-tokens/mcp-usage endpoint is `adminOnly`
  // server-side, so this row is filtered out below for a non-admin org member
  // (isAccountAdmin) — otherwise it's a Generate button that only ever 403s.
  { path: "mcp", key: "nav.mcp", icon: Bot },
  { path: "disclosure", key: "nav.disclosure", icon: Eye },
  { path: "visibility-mode", key: "nav.visibility-mode", icon: Shield },
  { path: "preferences", key: "nav.preferences", icon: Globe },
  { path: "plan", key: "nav.plan", icon: Gem },
];

// Nav surfaces whose data is filtered by the active department/team scope
// (?team=). Marked in the sidebar (a left scope line) so it's clear which pages
// reflect the current team selection. The aggregate views scope to the team's
// members; Modules scopes to the modules the team works on. Config-centric pages
// (teams, repos, …) ignore the scope.
const SCOPE_AWARE_PATHS = new Set(["", "daily", "ambient", "constellation", "logbook", "modules"]);

export default function Sidebar({ onClose }: { onClose?: () => void }) {
  const location = useLocation();
  const { account: urlAccount } = useParams<{ account: string }>();
  // Shared-Galaxies guest (read-only): trim the nav to the observation reads and
  // hide the owner/write surfaces (settings group, observe, member management).
  const isGuest = useIsSharedGuest();
  const selectedAccount = useAtomValue(selectedAccountAtom);
  const auth = useAtomValue(authAtom);
  const currentAccount = urlAccount || selectedAccount || auth.user?.login || "";
  // Carry the active department/team scope (?team=) across sidebar navigation so
  // a page switch doesn't drop the selection. URL stays the source of truth;
  // a galaxy switch rebuilds the path without the query (GalaxySwitcher), so the
  // scope can't leak across galaxies.
  const teamScopeParam = new URLSearchParams(location.search).get("team");
  const scopeSuffix = teamScopeParam ? `?team=${teamScopeParam}` : "";
  const locale = useAtomValue(localeAtom);
  const worker = useAtomValue(workerAtom);
  const t = useT(locale);

  // Worker heartbeat + queue status
  const [workerAlive, setWorkerAlive] = useState<boolean | null>(null);
  const [queueSize, setQueueSize] = useState(0);
  const [queueJobs, setQueueJobs] = useState<{ type: string; account_login: string }[]>([]);

  // Count of ON repos on the active galaxy that exceed the plan size cap and so
  // are skipped by the worker. Surfaced as a warning on the Repositories nav row
  // so an "on but not observed" repo is noticeable. Galaxy-scoped, refetched on
  // galaxy switch; silent on error (just no badge). Mirrors the billing fetch.
  const [reposOversize, setReposOversize] = useState(0);
  useEffect(() => {
    if (!currentAccount) return;
    const ac = new AbortController();
    repoApi
      .health(currentAccount)
      .then((h) => {
        if (!ac.signal.aborted) setReposOversize(h.oversize_active);
      })
      .catch((e) => {
        if (!ac.signal.aborted) console.error("sidebar repo health fetch failed", e);
      });
    return () => ac.abort();
  }, [currentAccount]);

  // The signed-in user's role in the active org galaxy — gates the Members nav
  // row. We show the row to any MEMBER of the org (admin or member), not just
  // admins: a non-admin lands on a read-only roster (MembersReadOnly) rather
  // than a missing menu, which read as "broken". Management stays admin-only,
  // enforced on the backend. Personal galaxies have no member surface.
  // urlAccount keeps the URL as the source of truth (galaxy-url-truth).
  const isOrgGalaxy = !!urlAccount && urlAccount !== auth.user?.login;
  const [activeOrgRole, setActiveOrgRole] = useState<string | null>(null);
  useEffect(() => {
    if (!isOrgGalaxy) {
      setActiveOrgRole(null);
      return;
    }
    const ac = new AbortController();
    orgApi
      .memberships()
      .then((orgs) => {
        if (!ac.signal.aborted) setActiveOrgRole(orgs.find((o) => o.login === urlAccount)?.role ?? null);
      })
      .catch((e) => {
        if (!ac.signal.aborted) console.error("sidebar org role fetch failed", e);
      });
    return () => ac.abort();
  }, [isOrgGalaxy, urlAccount]);
  // activeOrgRole is non-null only when the user is a member of this org galaxy.
  const showMembers = isOrgGalaxy && activeOrgRole !== null;
  // AI Integration (mcp) nav row: mcp-tokens / mcp-usage are all `adminOnly`
  // server-side (RequireAccountRole), so a non-admin org member only ever sees
  // a Generate button that 403s. A personal galaxy's own membership is created
  // with role "admin" (see VisibilityMode), so it's always visible there;
  // for an org galaxy this mirrors the Members row's admin check.
  const isAccountAdmin = !isOrgGalaxy || activeOrgRole === "admin";

  useEffect(() => {
    const check = () => {
      snapshotApi.workerStatus().then((s) => {
        setWorkerAlive(s.alive);
        setQueueSize(s.queue_size);
        setQueueJobs(s.jobs || []);
      }).catch(() => setWorkerAlive(null));
    };
    check();
    const interval = setInterval(check, 15000);
    return () => clearInterval(interval);
  }, []);

  // 設定 group — an inline accordion that expands its config links downward
  // inside the (now scrollable) nav. It used to fly out to the SIDE (left-full),
  // which broke once the nav became a scroll container: overflow-y clips the
  // x-axis too, so a side-anchored panel was cut off. Expanding downward keeps
  // it inside the scroller and consistent with the other within-width popovers.
  const [settingsMenuOpen, setSettingsMenuOpen] = useState(false);
  const settingsActive = settingsLinks.some(
    (s) => location.pathname === `/${currentAccount}/${s.path}`,
  );
  // Expand the group when you're on (or deep-link to) one of its config pages,
  // so the active child is visible without a manual click.
  useEffect(() => {
    if (settingsActive) setSettingsMenuOpen(true);
  }, [settingsActive]);

  return (
    <aside
      // The aside is JUST the scroll container — the WHOLE sidebar scrolls as one
      // (not a nested nav-only scroller, which left the header/footer as dead
      // wheel zones and a boundary that "caught"). h-full caps it at the viewport
      // so it never grows the document. The flex column + sticky footer live on
      // the inner wrapper below, NOT here: combining overflow-y-auto with a flex
      // auto-margin on the same element makes the auto-margin resolve flakily, so
      // the footer sometimes failed to drop to the bottom. overscroll-contain
      // keeps the drawer's scroll from chaining to the page behind it on mobile,
      // so a swipe over the open menu never scrolls the content underneath.
      className="surface-dark h-full overflow-y-auto overscroll-contain"
      style={{
        borderRight: "1px solid var(--border)",
        backdropFilter: "blur(12px)",
        WebkitBackdropFilter: "blur(12px)",
      }}
    >
      {/* Inner column is at least as tall as the aside (min-h-full), so the
          mt-auto footer pins to the bottom when content is short, and the column
          just grows past the viewport (aside scrolls) when it's tall. */}
      <div className="flex min-h-full flex-col">
      <div className="flex items-center justify-between p-lg">
        <div className="flex items-center gap-md">
          <AceLogoMark glow="chrome" className="h-10 w-10" />
          <h1
            style={{
              fontFamily: "var(--display)",
              fontSize: "1.125rem",
              fontWeight: 700,
              letterSpacing: "-0.02em",
              color: "var(--text-strong)",
            }}
          >
            OrbitLens{" "}
            <span style={{ fontWeight: 600, color: "var(--gold)" }}>Ace</span>
          </h1>
        </div>
        <button
          onClick={onClose}
          className="transition-colors lg:hidden"
          style={{ color: "var(--text-muted)" }}
          onMouseEnter={(e) => {
            e.currentTarget.style.color = "var(--text-strong)";
          }}
          onMouseLeave={(e) => {
            e.currentTarget.style.color = "var(--text-muted)";
          }}
        >
          ✕
        </button>
      </div>
      <GalaxySwitcher />
      {/* Department/team scope narrows every galaxy view below galaxy level.
          Org galaxies only (a personal galaxy has no sub-teams), and gated by the
          same launch flag as the Teams page. */}
      {isOrgGalaxy && <TeamScopeSwitcher />}
      {/* No own scroll — the whole aside scrolls as one (see above). The footer
          group below uses mt-auto to sit at the bottom when everything fits. */}
      <nav className="space-y-xs px-md">
        {/* Members (seat + invite management) is org-admin only, so it isn't in
            the static navKeys — it's spliced in directly under Teams once the
            active org role resolves. */}
        {(showMembers
          ? navKeys.flatMap((item) =>
              item.path === "teams"
                ? [item, { path: "members", key: "nav.members" as MessageKey, icon: Users }]
                : [item],
            )
          : navKeys
        )
          .filter((item) => !isGuest || GUEST_NAV_PATHS.has(item.path))
          .map((item) => {
          const fullPath = `/${currentAccount}${item.path ? `/${item.path}` : ""}`;
          const isActive = item.path === ""
            ? location.pathname === `/${currentAccount}` || location.pathname === `/${currentAccount}/`
            : location.pathname.startsWith(fullPath);
          // Scope-aware items carry a left accent line: faint aqua = "responds to
          // team scope", bright aqua + glow = "filtered by the active scope now".
          // The active-page gold border still wins the border colour; the glow
          // layers on top so an active scoped page reads as both.
          const scopeAware = SCOPE_AWARE_PATHS.has(item.path);
          const scoped = !!teamScopeParam;
          const scopeBorder = scopeAware
            ? scoped
              ? "var(--color-gruvbox-aqua)"
              : "color-mix(in oklch, var(--color-gruvbox-aqua) 32%, transparent)"
            : "transparent";
          if (item.enabled === false) {
            // Disabled feature: visible but inert, with a "準備中 / Soon" badge.
            return (
              <div
                key={item.path}
                className="flex items-center gap-md px-md py-md text-sm"
                style={{
                  fontWeight: 400,
                  color: "var(--text-dim)",
                  borderLeft: "2px solid transparent",
                  paddingLeft: "12px",
                  cursor: "not-allowed",
                }}
                title={t("comingSoon.title")}
                aria-disabled="true"
              >
                <span
                  className="flex items-center justify-center"
                  style={{ opacity: 0.4 }}
                >
                  <item.icon size={16} strokeWidth={1.75} />
                </span>
                <span style={{ opacity: 0.7 }}>{t(item.key)}</span>
                <span
                  className="ml-auto text-[9px] uppercase"
                  style={{
                    fontFamily: "var(--mono)",
                    letterSpacing: "0.1em",
                    color: "var(--gold)",
                    opacity: 0.75,
                  }}
                >
                  {t("common.soon")}
                </span>
              </div>
            );
          }
          return (
            <Link
              key={item.path}
              to={`${fullPath}${scopeSuffix}`}
              onClick={onClose}
              title={
                scopeAware
                  ? scoped
                    ? t("sidebar.scopeFiltered")
                    : t("sidebar.scopeAware")
                  : undefined
              }
              className="group flex items-center gap-md px-md py-md text-sm transition-colors duration-150"
              style={{
                fontWeight: isActive ? 500 : 400,
                color: isActive ? "var(--text-strong)" : "var(--text-muted)",
                borderLeft: "2px solid",
                borderLeftColor: isActive ? "var(--gold)" : scopeBorder,
                // Aqua left glow when this page is filtered by the active scope.
                boxShadow: scopeAware && scoped ? "inset 5px 0 7px -5px var(--color-gruvbox-aqua)" : undefined,
                paddingLeft: "12px",
              }}
              onMouseEnter={(e) => {
                if (!isActive) e.currentTarget.style.color = "var(--text-strong)";
              }}
              onMouseLeave={(e) => {
                if (!isActive) e.currentTarget.style.color = "var(--text-muted)";
              }}
            >
              <span
                className="flex items-center justify-center"
                style={{
                  color: isActive ? "var(--gold)" : "inherit",
                  opacity: isActive ? 1 : 0.6,
                }}
              >
                <item.icon size={16} strokeWidth={1.75} />
              </span>
              {t(item.key)}
            </Link>
          );
        })}

        {/* 設定 group — a row that expands its config surfaces (repos, EIS
            config, disclosure, visibility, plan) inline below it, so they don't
            each take a permanent flat nav row but still live inside the
            scrollable nav (a side flyout would be clipped by the nav's
            overflow). Hidden for a read-only guest — every item is owner/write. */}
        {!isGuest && (
        <div>
          <button
            type="button"
            onClick={() => setSettingsMenuOpen((v) => !v)}
            aria-expanded={settingsMenuOpen}
            className="group flex w-full items-center gap-md px-md py-md text-sm transition-colors duration-150"
            style={{
              fontWeight: settingsActive ? 500 : 400,
              color: settingsActive || settingsMenuOpen ? "var(--text-strong)" : "var(--text-muted)",
              borderLeft: "2px solid",
              borderLeftColor: settingsActive ? "var(--gold)" : "transparent",
              paddingLeft: "12px",
            }}
          >
            <span
              className="flex items-center justify-center"
              style={{ color: settingsActive ? "var(--gold)" : "inherit", opacity: settingsActive ? 1 : 0.6 }}
            >
              <Settings size={16} strokeWidth={1.75} />
            </span>
            {t("nav.settings")}
            {/* Repos-oversize warning rolls up to the header so it's visible
                while the group is collapsed. */}
            {reposOversize > 0 && (
              <span
                className="ml-auto flex items-center text-gruvbox-yellow"
                title={interp(t("sidebar.reposOversize"), { count: reposOversize })}
              >
                <AlertTriangle size={13} strokeWidth={2} />
              </span>
            )}
            {/* Chevron points right when collapsed, rotates down when expanded. */}
            <ChevronRight
              className={`h-3.5 w-3.5 flex-shrink-0 transition-transform ${reposOversize > 0 ? "ml-sm" : "ml-auto"} ${settingsMenuOpen ? "rotate-90" : ""}`}
              style={{ color: "var(--text-dim)" }}
            />
          </button>
          {settingsMenuOpen && (
            <div className="mt-xs space-y-xs">
              {settingsLinks
                .filter((s) => s.path !== "mcp" || isAccountAdmin)
                .map((s) => {
                  const sActive = location.pathname === `/${currentAccount}/${s.path}`;
                  return (
                    <Link
                      key={s.path}
                      to={`/${currentAccount}/${s.path}`}
                      onClick={onClose}
                      // Indented one icon-width past the parent rows so the group
                      // reads as nested. The left rule turns gold on the active child.
                      className="flex items-center gap-md py-sm pr-md text-sm transition-colors"
                      style={{
                        color: sActive ? "var(--text-strong)" : "var(--text-muted)",
                        borderLeft: "2px solid",
                        borderLeftColor: sActive ? "var(--gold)" : "transparent",
                        paddingLeft: "34px",
                      }}
                      onMouseEnter={(e) => {
                        if (!sActive) e.currentTarget.style.color = "var(--text-strong)";
                      }}
                      onMouseLeave={(e) => {
                        if (!sActive) e.currentTarget.style.color = "var(--text-muted)";
                      }}
                    >
                      <s.icon className="h-4 w-4 flex-shrink-0" style={{ opacity: sActive ? 1 : 0.6 }} />
                      {t(s.key)}
                      {s.path === "repos" && reposOversize > 0 && (
                        <span
                          className="ml-auto flex items-center text-gruvbox-yellow"
                          title={interp(t("sidebar.reposOversize"), { count: reposOversize })}
                        >
                          <AlertTriangle size={13} strokeWidth={2} />
                        </span>
                      )}
                    </Link>
                  );
                })}
            </div>
          )}
        </div>
        )}
      </nav>

      {/* Worker health — always visible. mt-auto pins this footer group (health
          + progress + account menu) to the bottom when the nav is short; it
          collapses to 0 when content overflows, so everything scrolls together. */}
      <div
        className="mt-auto mx-md mb-md px-md py-sm"
        style={{ fontFamily: "var(--mono)" }}
      >
        {/* The worker is scale-to-zero (on-demand Fargate RunTask), so "not
            alive" is the normal idle state — never an error. Only a queued job
            with no worker shows the transient "starting" (amber); a quiet
            queue + no worker is just "idle" (neutral). */}
        <div className="flex items-center gap-md">
          <span
            className="h-1.5 w-1.5 rounded-full"
            style={{
              backgroundColor:
                workerAlive === null
                  ? "var(--text-dim)"
                  : workerAlive
                  ? "var(--color-gruvbox-green)"
                  : queueSize > 0
                  ? "var(--gold)"
                  : "var(--text-dim)",
            }}
          />
          <span
            className="text-[10px] uppercase"
            style={{
              letterSpacing: "0.1em",
              color:
                workerAlive === false && queueSize > 0
                  ? "var(--gold)"
                  : "var(--text-muted)",
            }}
          >
            {workerAlive === null
              ? "worker ·"
              : workerAlive
              ? "worker · online"
              : queueSize > 0
              ? "worker · starting"
              : "worker · idle"}
          </span>
          {queueSize > 0 && (
            <span
              className="text-[10px] tabular-nums"
              style={{ color: "var(--gold)" }}
            >
              Q:{queueSize}
            </span>
          )}
        </div>
        {queueJobs.length > 0 && (
          <div className="mt-sm space-y-xs">
            {queueJobs.map((job, i) => (
              <div
                key={i}
                className="flex items-center gap-sm text-[9px]"
                style={{ color: "var(--text-dim)" }}
              >
                <span style={{ color: "var(--gold)" }}>&#x25CB;</span>
                <span className="truncate">{job.account_login}</span>
                <span>({job.type})</span>
              </div>
            ))}
          </div>
        )}
      </div>

      {/* Worker progress — shown during analysis */}
      {worker.phase !== "idle" && (
        <div
          className="mx-md mb-md px-md py-md"
          style={{
            border: "1px solid var(--border)",
            borderRadius: "6px",
            fontFamily: "var(--mono)",
          }}
        >
          <div className="flex items-center gap-md">
            {worker.active ? (
              <span className="relative flex h-2 w-2">
                <span
                  className="absolute inline-flex h-full w-full animate-ping rounded-full opacity-75"
                  style={{ backgroundColor: "var(--gold)" }}
                />
                <span
                  className="relative inline-flex h-2 w-2 rounded-full"
                  style={{ backgroundColor: "var(--gold)" }}
                />
              </span>
            ) : worker.phase === "completed" ? (
              <span
                className="h-2 w-2 rounded-full"
                style={{ backgroundColor: "var(--color-gruvbox-green)" }}
              />
            ) : worker.phase === "failed" ? (
              <span
                className="h-2 w-2 rounded-full"
                style={{ backgroundColor: "var(--color-gruvbox-red)" }}
              />
            ) : null}
            <span
              className="text-[10px] uppercase truncate"
              style={{
                letterSpacing: "0.1em",
                color:
                  worker.phase === "completed"
                    ? "var(--color-gruvbox-green)"
                    : worker.phase === "failed"
                    ? "var(--color-gruvbox-red)"
                    : "var(--text-strong)",
              }}
            >
              {worker.phase === "completed"
                ? t("sidebar.workerComplete")
                : worker.phase === "failed"
                  ? t("sidebar.workerFailed")
                  : t("sidebar.workerObserving")}
            </span>
          </div>
          {worker.active && worker.total > 0 && (
            <div
              className="mt-sm h-1 w-full overflow-hidden rounded-full"
              style={{ backgroundColor: "var(--border)" }}
            >
              <div
                className="h-full rounded-full transition-all duration-500"
                style={{
                  width: `${Math.round((worker.step / worker.total) * 100)}%`,
                  backgroundColor: "var(--gold)",
                }}
              />
            </div>
          )}
          {worker.active && worker.detail && (
            <p
              className="mt-sm truncate text-[10px]"
              style={{ color: "var(--text-dim)" }}
            >
              {worker.detail}
            </p>
          )}
        </div>
      )}

      {/* Identity row (avatar popover + JP/EN + theme) — shared with the
          ArchiveLayout aside so both chromes keep the same bottom-left account
          menu. Owns its own state/atoms, so nothing is threaded through here. */}
      <AccountMenu />
      </div>
    </aside>
  );
}
